import { useCallback, useEffect } from 'react'
import AppRouter from './components/global/AppRouter'
import ThemeWrapper from './features/theme/components/ThemeWrapper'
import { createLogger } from './app/logger'
import useAppDispatch from './hooks/useAppDispatch'
import useAppSelector from './hooks/useAppSelector'
import { getDarkModeSelector, setDarkMode } from './features/theme/themeSlice'
import { makeStyles } from 'tss-react/mui'
import important from './utils/important'
import clsx from 'clsx'
import { Box } from '@mui/material'
import {
  ChronoAppFooter,
  ChronoAppHeader,
  ChronoThemeSwitch,
} from '@eventops/chronotrack-ctlive-mui'
import { register as serviceWorkerRegister, checkForUpdates } from './serviceWorkerRegistration'
import NewAppVersionModal from './components/global/NewAppVersionModal'
import { getIsServiceWorkerAvailableSelector } from './features/serviceWorker/serviceWorkerSlice'

// eslint-disable-next-line no-unused-vars
const logger = createLogger({
  prefix: 'App',
})

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
  },
  appHeader: {
    position: 'relative',
    width: important('auto'),
    [theme.breakpoints.down('lg')]: {
      marginLeft: 0,
      marginRight: 0,
    },
    '& .MuiToolbar-gutters': {
      margin: important(0),
    },
  },
  content: {
    flexGrow: 1,
    width: '100%',
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    maxWidth: '90vw',
    [theme.breakpoints.down('lg')]: {
      maxWidth: '100vw',
      marginLeft: 0,
      marginRight: 0,
    },
  },
  contentShift: {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.up('lg')]: {
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    [theme.breakpoints.down('lg')]: {
      marginLeft: 0,
      marginRight: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  // necessary for content to be below app bar
  toolbar: theme.mixins.toolbar as any,
}))

function App() {
  const dispatch = useAppDispatch()
  const { classes } = useStyles()
  const darkMode = useAppSelector(getDarkModeSelector)
  const isServiceWorkerAvailable = useAppSelector(getIsServiceWorkerAvailableSelector)

  const toggleDarkMode = useCallback(
    (darkMode: boolean) => {
      dispatch(setDarkMode(darkMode))
    },
    [dispatch]
  )

  useEffect(() => {
    // If you want your app to work offline and load faster, you can change
    // unregister() to register() below. Note this comes with some pitfalls.
    // Learn more about service workers: https://cra.link/PWA
    serviceWorkerRegister({ dispatch })
  }, [dispatch])

  useEffect(() => {
    const handle = setInterval(() => {
      logger.info('polling for sw updates')
      checkForUpdates({ dispatch })
    }, 30000) // polling every half minute

    return () => clearInterval(handle)
  }, [dispatch])

  return (
    <ThemeWrapper>
      <div className={classes.container}>
        <main className={clsx(classes.content, classes.contentShift)}>
          <Box
            display='flex'
            flexDirection='row'
            justifyContent='space-between'
            paddingRight={2}
          >
            <ChronoAppHeader
              darkMode={darkMode}
              className={classes.appHeader}
              hasDrawer={false}
            />
            <ChronoThemeSwitch
              darkMode={darkMode}
              toggleDarkMode={toggleDarkMode}
            />
          </Box>
          <AppRouter />
          {isServiceWorkerAvailable && <NewAppVersionModal />}
          <ChronoAppFooter injectedStatusPage={''} />
        </main>
      </div>
    </ThemeWrapper>
  )
}

export default App
